import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp, History, ImagePlus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAskAi } from '../../assistant/AskAiContext';

/**
 * Where the vendor talks to their team.
 *
 * The biggest thing on Home, on purpose: the backoffice's front door is now a
 * question, not a menu. Images attach through the same upload the side panel
 * uses, so a photo sent here is one the assistant can act on.
 *
 * `hero` is Home's size — tall, centred, with room to think; `dock` is the
 * conversation page's, pinned under the thread.
 */
export const Composer: React.FC<{
    variant?: 'hero' | 'dock';
    placeholder?: string;
    onSubmit: (text: string) => void;
    onRecents?: () => void;
    autoFocus?: boolean;
    disabled?: boolean;
    /**
     * Text put in the box from outside - a ready-made agent's brief. The nonce
     * makes picking the same template twice put it back after an edit.
     */
    seed?: { text: string; nonce: number } | null;
    className?: string;
}> = ({ variant = 'hero', placeholder, onSubmit, onRecents, autoFocus, disabled, seed, className }) => {
    const { t } = useTranslation();
    const askAi = useAskAi();
    const [value, setValue] = useState('');
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const hero = variant === 'hero';

    // Grows with what is typed, up to a limit, then scrolls.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, hero ? 240 : 180)}px`;
    }, [value, hero]);

    useEffect(() => {
        if (autoFocus) inputRef.current?.focus();
    }, [autoFocus]);

    // A template's brief lands in the box, focused with the caret at the end,
    // for the vendor to adapt and send - never sent on their behalf.
    useEffect(() => {
        if (!seed) return;
        setValue(seed.text);
        window.setTimeout(() => {
            const el = inputRef.current;
            if (!el) return;
            el.focus();
            el.setSelectionRange(seed.text.length, seed.text.length);
        }, 0);
    }, [seed]);

    const attachments = askAi?.attachments ?? [];
    const canSend = !disabled && !askAi?.sending && (value.trim().length > 0 || attachments.length > 0);

    const submit = () => {
        if (!canSend) return;
        onSubmit(value.trim());
        setValue('');
    };

    return (
        <div
            className={cn(
                'relative z-10 flex w-full flex-col rounded-2xl border bg-background transition-colors focus-within:border-foreground/40',
                className,
                // The same lift in both sizes: the dock floats over the thread
                // the way the hero sits on Home.
                'shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)]',
                hero ? 'gap-3 p-4' : 'gap-2 px-4 pt-3.5 pb-3',
            )}
        >
            {!!attachments.length && (
                <div className="flex flex-wrap gap-2">
                    {attachments.map((a) => (
                        <span key={a.url} className="relative">
                            <img src={a.url} alt={a.name} className="size-14 rounded-lg border object-cover" />
                            <button
                                type="button"
                                onClick={() => askAi?.removeAttachment(a.url)}
                                aria-label={t('AGENTS.COMPOSER.REMOVE_IMAGE', 'Remove image') as string}
                                className="absolute -end-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full border bg-background"
                            >
                                <X className="size-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <textarea
                ref={inputRef}
                value={value}
                rows={hero ? 2 : 1}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        submit();
                    }
                }}
                placeholder={placeholder ?? (t('AGENTS.COMPOSER.PLACEHOLDER', 'Ask your team anything…') as string)}
                aria-label={t('AGENTS.COMPOSER.LABEL', 'Message your team') as string}
                className={cn(
                    'w-full resize-none bg-transparent outline-none placeholder:text-muted-foreground',
                    hero ? 'min-h-[56px] text-base' : 'min-h-[44px] text-sm',
                )}
            />

            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void askAi?.attach(file);
                            e.target.value = '';
                        }}
                    />
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => fileRef.current?.click()}
                                disabled={askAi?.uploading}
                                aria-label={t('AGENTS.COMPOSER.ATTACH', 'Attach an image') as string}
                            >
                                <ImagePlus className="size-4.5 text-muted-foreground" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('AGENTS.COMPOSER.ATTACH', 'Attach an image')}</TooltipContent>
                    </Tooltip>
                </div>

                <div className="flex items-center gap-1.5">
                    {onRecents && (
                        <Button variant="ghost" size="sm" onClick={onRecents} className="text-muted-foreground">
                            <History data-icon="inline-start" />
                            {t('AGENTS.COMPOSER.RECENTS', 'Recents')}
                        </Button>
                    )}
                    <Button
                        size="icon-sm"
                        onClick={submit}
                        disabled={!canSend}
                        aria-label={t('AGENTS.COMPOSER.SEND', 'Send') as string}
                        className="rounded-full"
                    >
                        <ArrowUp className="size-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};
